import { useEffect, useState } from 'react'
import { Input } from '../../../components/Input/Input'
import { Animation, Button, ButtonsContainer, Flex, ItemsGrid, TabFooter, ThinSeparator } from '../../../styling/GlobalStyles'
import { CancelBtn } from 'components/Button/CancelBtn'
import { ISelectValue } from 'constants/globalTypes'
import { IItemsTab } from 'types/inventory.interface'
import { IInvUnitsDraft } from 'types/inventoryOps.interface'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { OtherText, TitleText } from 'styling/Typography'
import { INV_ITEM_UNITS } from '../inventoryDemoData'

const UNIT_OPTIONS = [
  { value: 1, label: 'each' },
  { value: 2, label: 'L' },
  { value: 3, label: 'kg' },
  { value: 4, label: 'pair' },
  { value: 5, label: 'Drum (208 L)' },
  { value: 6, label: 'Box (5 kg)' },
  { value: 7, label: 'Carton (50 pr)' },
]

const unitOptionByLabel = (label: string): ISelectValue | null =>
  UNIT_OPTIONS.find((o) => o.label === label) ?? null

const emptyUnitsDraft: IInvUnitsDraft = {
  purchaseUnit: null,
  stockUnit: null,
  issueUnit: null,
  factor: '',
}

export const InventoryItemsReplenishmentTab = ({
  state,
  setState,
  setActive,
}: IItemsTab) => {
  const [units, setUnits] = useState<IInvUnitsDraft>(emptyUnitsDraft)
  const [unitsTouched, setUnitsTouched] = useState(false)

  useEffect(() => {
    if (unitsTouched) return
    const preset = state?.sku ? INV_ITEM_UNITS[state.sku] : undefined
    if (!preset) return
    setUnits({
      purchaseUnit: unitOptionByLabel(preset.purchaseUnit),
      stockUnit: unitOptionByLabel(preset.stockUnit),
      issueUnit: unitOptionByLabel(preset.issueUnit),
      factor: String(preset.factor),
    })
  }, [state?.sku, unitsTouched])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setState((prev) => ({ ...prev, [name]: value }))
  }

  const handleSelect = async (value: ISelectValue, name: string) => {
    const copy = structuredClone(state)
    copy[name] = value
    setState(copy)
  }

  const handleUnitSelect = (value: ISelectValue, name: string) => {
    setUnitsTouched(true)
    setUnits((prev) => ({ ...prev, [name]: value }))
  }

  const handleFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setUnitsTouched(true)
    setUnits((prev) => ({ ...prev, factor: value }))
  }

  const handleNext = () => {
    setActive('Financial')
  }

  return (
    <Animation>

      <Flex column width='100%' >
        <ItemsGrid columns={3} gap='1rem'>
          <HonestSelectAsyncInput
            link='inventory-suppliers'
            name='preferredSupplier'
            labelText='Supplier'
            placeholder='Select Supplier'
            defaultValue={state?.preferredSupplier?.label}
            onChange={handleSelect}
            parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
            nomarg
          />
          <Input
            value={state?.reorderPoint}
            name='reorderPoint'
            onChange={handleChange}
            type='number'
            placeholder='15'
            labelText='Reorder Point'
          />

          <Input
            value={state?.leadTimeDays}
            name='leadTimeDays'
            onChange={handleChange}
            type='number'
            placeholder='15'
            labelText='Lead Time Days'
          />
          <Input
            value={state?.minLevel}
            name='minLevel'
            onChange={handleChange}
            type='number'
            placeholder='Enter minimum level'
            labelText='Minimum Level'
          />
          <Input
            value={state?.maxLevel}
            name='maxLevel'
            onChange={handleChange}
            type='number'
            placeholder='Enter maximum level'
            labelText='Maximum Level'
          />

        </ItemsGrid>
        <ThinSeparator />
        <Flex column width='100%' gap='0.5rem'>
          <TitleText weight='bold'>Units</TitleText>
          <ItemsGrid columns={4} gap='1rem'>
            <HonestSelectInput
              key={'purchaseUnit-' + (state?.sku ?? 'new')}
              name='purchaseUnit'
              labelText='Purchase Unit'
              placeholder='Select unit'
              options={UNIT_OPTIONS}
              defaultValue={units.purchaseUnit?.value}
              onChange={handleUnitSelect}
              nomarg
              noX
            />
            <HonestSelectInput
              key={'stockUnit-' + (state?.sku ?? 'new')}
              name='stockUnit'
              labelText='Stock Unit'
              placeholder='Select unit'
              options={UNIT_OPTIONS}
              defaultValue={units.stockUnit?.value}
              onChange={handleUnitSelect}
              nomarg
              noX
            />
            <HonestSelectInput
              key={'issueUnit-' + (state?.sku ?? 'new')}
              name='issueUnit'
              labelText='Issue Unit'
              placeholder='Select unit'
              options={UNIT_OPTIONS}
              defaultValue={units.issueUnit?.value}
              onChange={handleUnitSelect}
              nomarg
              noX
            />
            <Input
              value={units.factor}
              name='factor'
              onChange={handleFactorChange}
              type='number'
              placeholder='1'
              labelText='Conversion Factor'
            />
          </ItemsGrid>
          <OtherText grey>
            1 purchase unit = {units.factor || '1'} stock units · Units are not saved to the backend yet.
          </OtherText>
        </Flex>
      </Flex>

      <TabFooter >
        <ButtonsContainer>
          <CancelBtn />
          <Button orange onClick={handleNext}>
            Next
          </Button>
        </ButtonsContainer>
      </TabFooter>
    </Animation>
  )
}
